import React, { useEffect, useState } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { CreateProjectStepsTitle } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStepsTitle/CreateProjectStepsTitle';
import { Button } from 'src/components/ui/button';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'src/store';
import { ProductsType } from 'src/constants/products';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import {
  useDeployConfigurationSettingsMutation,
  useGetAllConfigurationSettingsQuery,
  useGetConfigurationSettingsPromptQuery,
  useUpdateConfigurationSettingsPromptMutation,
} from 'src/services';
import moment from 'moment';
import { useLoader } from 'src/provider/LoaderProvider';
import { useDispatch } from 'react-redux';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { array, object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { CreateProjectHead } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectHead/CreateProjectHead';
import { Skeleton } from 'src/components/ui/skeleton';
import * as yup from 'yup';
import { validateWorkflowActions } from 'src/helpers/validateWorkflowActions';
import { AddWorkflowPopup } from 'src/pages/ConfigurationPage/WorkflowsTab/AddWorkflowPopup/AddWorkflowPopup';
import { WorkflowsTable } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowsTable/WorkflowsTable';
import { API_VERSION } from 'src/constants/api-routes';
import { useConfigurationContext } from 'src/provider/ConfigurationProvider';
import { EditWorkflowBlock } from 'src/pages/ConfigurationPage/WorkflowsTab/EditWorkflowBlock/EditWorkflowBlock';

const LoadingSkeleton = () => {
  return (
    <div className={'w-[455px] flex flex-col gap-5'}>
      <Skeleton className={'w-full h-4 mb-10'} />
      <Skeleton className={'w-full h-20'} />
      <Skeleton className={'w-full h-52'} />
    </div>
  );
};

const CreateProjectStep7Page = () => {
  const [settingsPage, setSettingsPage] = useState(1);
  const [settings, setSettings] = useState(null);
  const [activeSettings, setActiveSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [inputData, setInputData] = useState(null);
  const [customWorkflows, setCustomWorkflows] = useState(inputData?.pf_custom_workflows || []);
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const { id, step } = useParams();
  const loader = useLoader();
  const navigate = useNavigate();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const projectTypeVar = projectType || type;
  const creationFlowState = useAppSelector((s) => s.creationFlowSlices);
  const { isEditWorkflowMode, setIsEditWorkflowMode, setSelectedWorkflow } =
    useConfigurationContext();
  const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;
  const isV1 = version === API_VERSION.V1;
  const dispatch = useDispatch();
  const { data: allConfigurationSettings, isLoading: isLoadingAllConfigurationSettings } =
    useGetAllConfigurationSettingsQuery(
      {
        id: id,
        page: settingsPage,
      },
      { skip: !Boolean(id) },
    );
  const [
    updateConfigurationSettings,
    { data: updateConfigurationSettingsData, isLoading: isLoadingUpdateConfigurationSettings },
  ] = useUpdateConfigurationSettingsPromptMutation();
  const { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt } =
    useGetConfigurationSettingsPromptQuery(
      { project_id: id, settings_id: activeSettings && activeSettings.id },
      {
        skip: !Boolean(id && activeSettings),
        refetchOnMountOrArgChange: true,
      },
    );
  const [
    deployConfigurationSettings,
    {
      isLoading: isLoadingDeployConfigurationSettings,
      isSuccess: isSuccessDeployConfigurationSettings,
    },
  ] = useDeployConfigurationSettingsMutation();

  useEffect(() => {
    if (
      isLoadingAllConfigurationSettings ||
      isLoadingConfigurationSettingsPrompt ||
      isLoadingUpdateConfigurationSettings ||
      isLoadingDeployConfigurationSettings ||
      settings === null
    ) {
      setIsLoading(true);
      loader.show();
    } else {
      setIsLoading(false);
      loader.hide();
    }
  }, [
    isLoadingAllConfigurationSettings,
    isLoadingConfigurationSettingsPrompt,
    isLoadingUpdateConfigurationSettings,
    isLoadingDeployConfigurationSettings,
    settings,
  ]);

  useEffect(() => {
    if (settings) {
      const config = creationFlowState[id];
      const settingsId = config.promptBuilderSettingsId;

      if (settingsId && settings.length) {
        const setting = settings.find((setting) => `${setting.id}` === `${settingsId}`);

        setting && setActiveSettings(setting);
      }
    }
  }, [settings, creationFlowState]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      setInputData((configurationSettingsPrompt as any).input_data);
    }
  }, [configurationSettingsPrompt]);

  useEffect(() => {
    if (allConfigurationSettings) {
      setSettings((prevState) => {
        const prev = prevState || [];
        const newState = [...prev];

        (allConfigurationSettings as any).items.forEach((item) => {
          const index = prev.findIndex((prevItem) => {
            return prevItem.id === item.id;
          });

          if (index > -1) {
            newState[index] = item;
          } else {
            newState.push(item);
          }
        });

        newState.sort((a, b) => +moment(b.created_at) - +moment(a.created_at));

        return newState;
      });

      if ((allConfigurationSettings as any).page_info.has_next_page) {
        setSettingsPage((allConfigurationSettings as any).page_info.page + 1);
      }
    }
  }, [allConfigurationSettings]);

  useEffect(() => {
    if (inputData) {
      methods.reset({
        pf_custom_workflows: inputData?.pf_custom_workflows || [],
      });

      setCustomWorkflows(inputData?.pf_custom_workflows || []);
    }
  }, [inputData]);

  useEffect(() => {
    const config = creationFlowState[id];

    if (config && +config.step < +step) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (updateConfigurationSettingsData) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
          finished: true,
        }),
      );

      deployConfigurationSettings({ project_id: id, settings_id: activeSettings.id });
    }
  }, [updateConfigurationSettingsData]);

  useEffect(() => {
    if (isSuccessDeployConfigurationSettings) {
      next();
    }
  }, [isSuccessDeployConfigurationSettings]);

  const methods = useForm({
    defaultValues: {
      pf_custom_workflows: inputData?.pf_custom_workflows || [],
    },
    resolver: yupResolver(
      yup.lazy((values) => {
        const baseSchema = {
          pf_custom_workflows: array(
            object({
              name: stringScheme({ required: false }),
              description: stringScheme({ required: false }),
            }),
          ),
        };

        return yup.object(baseSchema);
      }),
    ),
  });

  const submit = (data) => {
    const is = {
      valid: isV1
        ? data.pf_custom_workflows.some((workflow) =>
            validateWorkflowActions(workflow, methods.setError),
          )
        : true,
    };

    if (data.pf_custom_workflows.length && !is.valid) {
      return;
    }

    if (activeSettings) {
      const body = {
        input_data: {
          ...inputData,
          pf_custom_workflows: customWorkflows,
        },
      };

      updateConfigurationSettings({ project_id: id, settings_id: activeSettings.id, body });
    }
  };

  const onWorkflowsChange = (data) => {
    setCustomWorkflows(data);
    methods.setValue('pf_custom_workflows', data);
  };

  const onWorkflowAdd = (workflow) => {
    if (!workflow) {
      return;
    }

    const newCustomWorkflows = [...customWorkflows, workflow];

    setCustomWorkflows(newCustomWorkflows);
    methods.setValue('pf_custom_workflows', newCustomWorkflows);
  };

  const next = () => {
    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/control-desk?type=${ProductsType.customer}`
        : `${PATH_DASHBOARD.ROOT}`,
      { replace: true },
    );
  };

  const back = () => {
    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/new/${+step - 1}?type=${projectTypeVar}`
        : `${PATH_DASHBOARD.PROJECTS}/${id}?type=${projectTypeVar}`,
      { replace: true },
    );
  };

  return (
    <React.Fragment>
      <PageContentWrapper
        menuBarVisible={false}
        className={'create-project-step items-center !gap-5'}
      >
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <CreateProjectStepsTitle step={isV1 ? +step : +step - 1} title={'Custom Workflows'} />
            <CreateProjectHead step={+step} titleIndex={7} />
            <div className={'flex flex-col items-center w-[455px] gap-5'}>
              <FormProvider {...methods}>
                <form
                  id={'create-project-step-7'}
                  className={'flex flex-col gap-5 w-full min-w-[455px] max-w-[455px]'}
                  onSubmit={methods.handleSubmit(submit)}
                >
                  {isEditWorkflowMode ? (
                    <EditWorkflowBlock
                      onWorkflowAdd={onWorkflowAdd}
                      onWorkflowsChange={(workflow, index) => {
                        const newWorkflows = [...customWorkflows];
                        newWorkflows[index] = workflow;

                        onWorkflowsChange(newWorkflows);
                      }}
                      compact={true}
                      readonly={false}
                    />
                  ) : (
                    <>
                      <WorkflowsTable
                        isCustom={true}
                        workflows={customWorkflows}
                        readonly={false}
                        compact={true}
                        onChange={(workflow) => onWorkflowsChange(workflow)}
                        rowClassName={'bg-white hover:bg-white-800'}
                      />
                      {isV1 ? (
                        <AddWorkflowPopup
                          onSubmit={onWorkflowAdd}
                          trigger={
                            <Button size={'xl'} variant={'purple'} className={'font-semibold'}>
                              Add Workflow
                            </Button>
                          }
                        />
                      ) : (
                        <Button
                          onClick={(event) => {
                            event.preventDefault();
                            setIsEditWorkflowMode(true);
                          }}
                          size={'xl'}
                          variant={'purple'}
                          className={'font-semibold'}
                        >
                          Add Workflow
                        </Button>
                      )}
                    </>
                  )}
                  <Button
                    size={'xl'}
                    form="create-project-step-7"
                    type="submit"
                    className="w-full flex flex-row h-12 mt-5 font-semibold"
                    disabled={!isV1 && isEditWorkflowMode}
                  >
                    Continue
                  </Button>
                  <div
                    onClick={back}
                    className={
                      'text-[#3F60B3] text-xs text-center font-medium cursor-pointer hover:underline'
                    }
                  >
                    ← Back
                  </div>
                </form>
              </FormProvider>
            </div>
          </>
        )}
      </PageContentWrapper>
    </React.Fragment>
  );
};

export default CreateProjectStep7Page;
